import { useCallback, useEffect, useState } from 'react';
import { myInvoices } from '@/api/subscription';

export interface SubGateState {
  loading: boolean;
  /** Invoice exists and is >= 15 days old — show reminder dialog. */
  hasDue: boolean;
  dueAmount: number;
  /** >= 32 days since invoice generation (+ any admin extension) → block orders. */
  isBlocked: boolean;
  /** Days since invoice generation (used for display). */
  daysSince: number;
  /** Admin-set extension days on the first (earliest billing-period) due invoice. */
  extensionDays: number;
  refresh: () => Promise<void>;
}

const REMIND_AFTER_DAYS = 15;
const BLOCK_AFTER_DAYS  = 32;

export function useSubscriptionGate(onDueFound?: (isBlocked: boolean) => void): SubGateState {
  const [loading,       setLoading]       = useState(true);
  const [hasDue,        setHasDue]        = useState(false);
  const [dueAmount,     setDueAmount]     = useState(0);
  const [isBlocked,     setIsBlocked]     = useState(false);
  const [daysSince,     setDaysSince]     = useState(0);
  const [extensionDays, setExtensionDays] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [pending, partial] = await Promise.all([
        myInvoices({ status: 'PENDING',        size: 50 }),
        myInvoices({ status: 'PARTIALLY_PAID', size: 50 }),
      ]);
      const all = [...pending.content, ...partial.content];

      if (!all.length) {
        setHasDue(false); setDueAmount(0); setIsBlocked(false);
        setDaysSince(0);  setExtensionDays(0);
        return;
      }

      const due = all.reduce((s, i) => s + Number(i.dueAmount ?? 0), 0);
      setDueAmount(due);

      // First (earliest billing period) unpaid invoice drives the check.
      // Use periodYear/periodMonth — immune to Spring Instant serialisation differences.
      const firstUnpaid = all.reduce((a, b) =>
        a.periodYear * 100 + a.periodMonth <= b.periodYear * 100 + b.periodMonth ? a : b
      );
      const ext = firstUnpaid.extensionDays ?? 0;
      setExtensionDays(ext);

      // Invoice is generated on the 1st of its billing period
      const invoiceDate = new Date(firstUnpaid.periodYear, firstUnpaid.periodMonth - 1, 1);
      const since = Math.floor((Date.now() - invoiceDate.getTime()) / 86_400_000);
      setDaysSince(since);

      const showReminder = since >= REMIND_AFTER_DAYS;
      const blocked      = since >= (BLOCK_AFTER_DAYS + ext);

      setHasDue(showReminder);   // only show dialog from day 15
      setIsBlocked(blocked);     // block from day 32

      if (showReminder) onDueFound?.(blocked);
    } catch {
      setHasDue(false); setIsBlocked(false); setDaysSince(0); setExtensionDays(0);
    } finally {
      setLoading(false);
    }
  }, [onDueFound]);

  useEffect(() => { void refresh(); }, [refresh]);

  return { loading, hasDue, dueAmount, isBlocked, daysSince, extensionDays, refresh };
}
